import React, { useEffect, useState } from "react"

import Container from "@mui/material/Container"
import Grid2 from "@mui/material/Grid2"

// ICONS

import { makeStyles } from "@mui/styles"
import { grey, blue } from "@mui/material/colors"

import DashboardCard from "./Components/DashboardCard"
import NegotiationsByTagChart from "./Components/NegotiationsByTagChart"
import LeadsByUTMChart from "./Components/LeadsByUTMChart"
import InactiveLeadsCard from "./Components/InactiveLeadsCard"
import UnreadedMessages from "./Components/UndeadedMessages"
import SimpleDashboardCard from "./Components/SimpleDashboardCard"

import FormControl from "@mui/material/FormControl"
import InputLabel from "@mui/material/InputLabel"
import MenuItem from "@mui/material/MenuItem"
import Select from "@mui/material/Select"

import api from "../../services/api"
import SalesByDayChart from "./Components/TotalSalesByDayChart"
import FailedSalesByReasonChart from "./Components/FailedSalesByReasonChart"

const useStyles = makeStyles((theme) => ({
    container: {
        paddingTop: theme.spacing(4),
        paddingBottom: theme.spacing(4),
    },
    fixedHeightPaper: {
        padding: theme.spacing(2),
        display: "flex",
        flexDirection: "column",
        height: 240,
        overflowY: "auto",
        ...theme.scrollbarStyles,
    },
    cardAvatar: {
        fontSize: "55px",
        color: grey[500],
        backgroundColor: "#ffffff",
        width: theme.spacing(7),
        height: theme.spacing(7),
    },
    cardTitle: {
        fontSize: "18px",
        color: blue[700],
    },
    cardSubtitle: {
        color: grey[600],
        fontSize: "14px",
    },
    alignRight: {
        textAlign: "right",
    },
    fullWidth: {
        width: "100%",
    },
    selectContainer: {
        width: "100%",
        textAlign: "left",
    },
    card1: {
        padding: theme.spacing(2),
        display: "flex",
        overflow: "auto",
        flexDirection: "column",
        height: "100%",
        backgroundColor: "#0094bb",
        color: "#eee",
    },
    card2: {
        padding: theme.spacing(2),
        display: "flex",
        overflow: "auto",
        flexDirection: "column",
        height: "100%",
        backgroundColor: "#748e9d",
        color: "#eee",
    },
    card3: {
        padding: theme.spacing(2),
        display: "flex",
        overflow: "auto",
        flexDirection: "column",
        height: "100%",
        backgroundColor: "#7f78e6",
        color: "#eee",
    },
    card4: {
        padding: theme.spacing(2),
        display: "flex",
        overflow: "auto",
        flexDirection: "column",
        height: "100%",
        backgroundColor: "#cc991b",
        color: "#eee",
    },
    card5: {
        padding: theme.spacing(2),
        display: "flex",
        overflow: "auto",
        flexDirection: "column",
        height: "100%",
        backgroundColor: "#434243",
        color: "#eee",
    },
    card6: {
        padding: theme.spacing(2),
        display: "flex",
        overflow: "auto",
        flexDirection: "column",
        height: "100%",
        backgroundColor: "#b87d77",
        color: "#eee",
    },
    card7: {
        padding: theme.spacing(2),
        display: "flex",
        overflow: "auto",
        flexDirection: "column",
        height: "100%",
        backgroundColor: "#7bc780",
        color: "#eee",
    },
    card8: {
        padding: theme.spacing(2),
        display: "flex",
        overflow: "auto",
        flexDirection: "column",
        height: "100%",
        backgroundColor: "#b05c38",
        color: "#eee",
    },
    card9: {
        padding: theme.spacing(2),
        display: "flex",
        overflow: "auto",
        flexDirection: "column",
        height: "100%",
        backgroundColor: "#bd3c58",
        color: "#eee",
    },
}))

const utmSourceColors = {
    instagram: "#E1306C",
    facebook: "#1877F2",
    tiktok: "#010101",
    qrcode: "#6c757d",
    linkedin: "#0077B5",
    google: "#DB4437",
    youtube: "#FF0000",
}

const reasons = {
    givedUp: "Produto/serviço fora da necessidade atual",
    preferCompetitor: "Optou pelo concorrente",
    notEnoughMoney: "Falta de orçamento",
    problemWithService: "Problemas no atendimento",
    noResponseFromClient: "Sem retorno do cliente",
    another: "Outro",
}

const Dashboard = () => {
    const classes = useStyles()
    const [data, setData] = useState({})

    const [period, setPeriod] = useState(7)
    const [notCompletedByReason, setNotCompletedByReason] = useState([])
    const [periodsAvailable, setPeriodsAvailable] = useState([])

    const handleChange = (e) => {
        setPeriod(e.target.value)
    }

    useEffect(() => {
        const domain = window.location.hostname

        // Domínios que têm acesso total
        const isInternalDomain =
            domain === "localhost" || domain === "painel.synccrm.com.br"

        if (isInternalDomain) {
            setPeriodsAvailable([7, 15, 30])
            return
        }

        // Calcular dias desde a atualização
        const updateDate = new Date("2025-05-10")
        const today = new Date()
        const diffTime = today.getTime() - updateDate.getTime()
        const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24))

        // Determinar períodos permitidos
        if (diffDays >= 30) {
            setPeriodsAvailable([7, 15, 30])
        } else if (diffDays >= 15) {
            setPeriodsAvailable([7, 15])
        } else if (diffDays >= 7) {
            setPeriodsAvailable([7])
        } else {
            setPeriodsAvailable([diffDays])
            setPeriod(diffDays)
        }
    }, [])

    async function getData() {
        try {
            const response = await api.get(`/dashboard/?days=${period}`)
            setData(response.data)
        } catch (error) {
            console.log(error)
        }
    }

    useEffect(() => {
        getData()
    }, [period])

    useEffect(() => {
        if (data.salesCompletedByReason) {
            setNotCompletedByReason(
                data.salesCompletedByReason.map((item) => {
                    return {
                        ...item,
                        reason: reasons[item.reason],
                    }
                }),
            )
        }
    }, [data])

    const formatMinutes = (totalMinutes) => {
        const hours = Math.floor(totalMinutes / 60)
        const minutes = totalMinutes % 60
        return `${hours}h ${minutes}m`
    }

    const formatBRL = (number) => {
        return new Intl.NumberFormat("pt-BR", {
            style: "currency",
            currency: "BRL",
        }).format(number)
    }

    return (
        <Container maxWidth="lg" className={classes.container}>
            <Grid2 container spacing={2} justifyContent="center">
                <Grid2 size={{ xs: 12 }}>
                    {!!periodsAvailable.length && (
                        <FormControl>
                            <InputLabel id="demo-simple-select-label">
                                Período
                            </InputLabel>
                            <Select
                                labelId="demo-simple-select-label"
                                id="demo-simple-select"
                                value={period}
                                label="Período"
                                onChange={handleChange}
                                size="small"
                            >
                                {periodsAvailable.map((day) => (
                                    <MenuItem key={day} value={day}>
                                        {day} dias
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    )}
                </Grid2>

                {/* <Grid2 size={{ xs: 12, md: 6, lg: 3 }}>
                    <SimpleDashboardCard
                        label="Novos Leads"
                        value={data.lastDayLeadCount || 0}
                        subtitle="nas últimas 24 horas"
                        endAddornment={
                            <Box p={2}>
                                <Icon
                                    color={grey[500]}
                                    width={32}
                                    icon="mdi:leads-outline"
                                />
                            </Box>
                        }
                    />
                </Grid2> */}

                <Grid2 size={{ xs: 12, md: 6, lg: 3 }}>
                    <DashboardCard
                        title="Novos leads"
                        value={data.lastDayLeadCount?.total || 0}
                        percentage={data.lastDayLeadCount?.percentage || 0}
                        timeframe={`${period} dias`}
                        data={data.lastDayLeadCount?.data}
                    />
                </Grid2>
                <Grid2 size={{ xs: 12, md: 6, lg: 3 }}>
                    <DashboardCard
                        title="Negociações pendentes"
                        value={data.pendingNegotiationsByPeriod?.total || 0}
                        percentage={
                            data.pendingNegotiationsByPeriod?.percentage || 0
                        }
                        timeframe={`${period} dias`}
                        data={data.pendingNegotiationsByPeriod?.data}
                    />
                </Grid2>
                <Grid2 size={{ xs: 12, md: 6, lg: 3 }}>
                    <DashboardCard
                        title="Negociações em andamento"
                        value={data.openTicketsByPeriod?.total || 0}
                        percentage={data.openTicketsByPeriod?.percentage || 0}
                        timeframe={`${period} dias`}
                        data={data.openTicketsByPeriod?.data}
                    />
                </Grid2>
                <Grid2 size={{ xs: 12, md: 6, lg: 3 }}>
                    <DashboardCard
                        title="Vendas concluídas"
                        value={data.salesDoneByPeriod?.total || 0}
                        percentage={data.salesDoneByPeriod?.percentage || 0}
                        timeframe={`${period} dias`}
                        data={data.salesDoneByPeriod?.data}
                    />
                </Grid2>
                <Grid2 size={{ xs: 12, md: 6, lg: 3 }}>
                    <SimpleDashboardCard
                        label="Taxa de Conversão"
                        value={`${(data.conversionRate || 0)?.toFixed(1)}%`}
                    />
                </Grid2>
                <Grid2 size={{ xs: 12, md: 6, lg: 3 }}>
                    <SimpleDashboardCard
                        label="Tempo médio de atendimento"
                        value={formatMinutes(data.averageResponseTime || 0)}
                    />
                </Grid2>

                <Grid2 size={{ xs: 12, md: 6, lg: 3 }}>
                    <SimpleDashboardCard
                        label="Vendas totais"
                        value={
                            formatBRL(data.totalSalesByDay?.totalSales || 0) ||
                            ""
                        }
                    />
                </Grid2>
                <Grid2 size={{ xs: 12, md: 6, lg: 3 }}>
                    <SimpleDashboardCard
                        label="Total de interações"
                        value={data.totalMessagesSentInPeriod || 0}
                    />
                </Grid2>

                <Grid2 size={{ xs: 12, md: 6 }}>
                    <NegotiationsByTagChart
                        data={data?.ticketCountByTag || []}
                    />
                </Grid2>
                <Grid2 size={{ xs: 12, md: 6 }}>
                    <LeadsByUTMChart
                        data={data.leadCountByUTMSource?.data.map((item) => {
                            return {
                                ...item,
                                color: utmSourceColors[item.name.toLowerCase()],
                            }
                        })}
                        totalLeads={data.leadCountByUTMSource?.totalLeads || 0}
                    />
                </Grid2>

                <Grid2 size={{ xs: 12, md: 6 }}>
                    <SalesByDayChart data={data.totalSalesByDay?.data || []} />
                </Grid2>
                <Grid2 size={{ xs: 12, md: 6 }}>
                    <FailedSalesByReasonChart data={notCompletedByReason} />
                </Grid2>

                <Grid2 size={{ xs: 12, md: 6 }}>
                    <InactiveLeadsCard
                        data={data.ticketsWaitingResponse || []}
                    />
                </Grid2>
                <Grid2 size={{ xs: 12, md: 6 }}>
                    <UnreadedMessages />
                </Grid2>
            </Grid2>
        </Container>
    )
}

export default Dashboard
